<template>
  <div class="PdfJs inherit-height">
    <div class="pdf-container inherit-height scroll-container no-overscroll">
      <div
        v-if="mx_isMobile && features.pdfViewerSwitchEnabled && displayPdfViewerHintMode !== 'none'"
        @click="toggleWarningHint"
        :style="pdfIssuesWarningStyle"
        class="pdf-viewer-issues i-warning"
        v-touch="{
          up: () => hideWarningHint(),
          down: () => showWarningHint(),
        }"
      >
        <template v-if="isWarningContentVisible">
          <div v-if="isWarningContentVisible" class="warning-close">
            <v-icon class="i-primary">mdi-close</v-icon>
          </div>
          <div class="text-h6">{{ $t('pdfIssuesTitle') }}</div>
          <div class="text-body1 pa-2 text-center">
            {{ isConnected && !mx_isIPhone ? $t('pdfIssuesHintOnline') : $t('pdfIssuesHintOffline') }}
          </div>
          <div class="px-2 full-width centered" style="justify-content: center">
            <v-btn rounded @click="downloadFile({ url: url, docTitle })" class="mr-3"
              >{{ $t('label.download') }}
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <!--            <v-btn rounded :href="url" class="mr-3"-->
            <!--              >{{ $t('label.download') }}-->
            <!--              <v-icon>mdi-download</v-icon>-->
            <!--            </v-btn>-->
            <v-btn v-if="isConnected && !mx_isIPhone" rounded class="ml-3 col-6" @click="togglePdfViewer">
              {{ $t('pdfViewerChange') }}
            </v-btn>
          </div>
          <div class="full-width pt-2 centered">Diesen Hinweis ...</div>
          <div @click.stop.prevent="" class="px-1 full-width" style="display: flex; flex-wrap: nowrap; align-items: center; justify-content: center">
            <v-chip-group active-class="i-primary" mandatory v-model="displayPdfViewerHintMode">
              <v-chip filter outlined value="display">immer anzeigen</v-chip>
              <v-chip filter outlined value="button">orange Linie</v-chip>
              <v-chip filter outlined value="none">nie anzeigen</v-chip>
            </v-chip-group>
          </div>
          <div class="full-width caption centered">(kann in Einstellungen angepasst werden)</div>
        </template>
      </div>

      <!-- PdfjsExpress -->
      <div ref="pdfJsExpressEl" id="pdfJsExpressEl" v-show="isPdfjsExpress" style="width: 100%"></div>

      <!-- Download -->
      <a class="full-width pt-4 ma-8" :href="url" target="_blank" v-if="!fullscreen && appliedPdfViewer === 'desktopembedded'">Download "{{ docTitle }}"</a>

      <!-- The Pdf.js iFrame -->
      <iframe v-if="!fullscreen && isPdfJs" id="pdfjs" class="no-overscroll" :src="iframeSrc" @load="pdfJsLoaded('pdfjs')"></iframe>
    </div>

    <v-icon v-if="isPdfJs" large :class="fullscreenBtnStyle" @click="toggleFullscreenMode"> mdi-fullscreen </v-icon>

    <v-dialog v-model="fullscreen" id="fullscreen-pdf" fullscreen>
      <div ref="container" class="inherit-height scroll-container no-overscroll" @click="toggleFullscreenMode">
        <!-- The Pdf.js iFrame -->
        <iframe
          v-if="fullscreen && isPdfJs"
          id="pdfjs"
          class="inherit-height no-overscroll"
          style="height: calc(100vh - 48px)"
          :src="iframeSrc"
          @load="pdfJsLoaded('pdfjs')"
        ></iframe>
      </div>

      <v-icon large :class="fullscreenBtnStyle" @click="toggleFullscreenMode"> mdi-fullscreen </v-icon>
    </v-dialog>
  </div>
</template>

<script>
// Good article on customizing the pdf.js viewer: https://pdfjs.express/blog/how-to-use-pdf-js
import config from '@/common/config';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import PDFJSExpress from '@pdftron/pdfjs-express-viewer';
import { mapState, mapMutations } from 'vuex';
import { PDFJS_EXPRESS, PDFJS, DISPLAY_PDF_VIEWER_HINT_MODE } from '@/common/constants';
import { downloadFile } from '@/common/networkUtils';

let scaleElement;
let displayChangeViewerTimer;
export default {
  name: 'PdfJs',

  mixins: [common, configMixin],
  props: {
    docTitle: String,
    url: String,
    page: String,
    loaderOffsetX: {
      type: Number,
      default: 400,
    },
    search: String,
    allowFullScreen: { type: Boolean, default: true },
    downloadFileName: String,
    hidePdfOpenIcon: { type: Boolean, default: false },
    hidePdfDownloadIcon: { type: Boolean, default: false },
  },

  data() {
    return {
      directDownload: true,
      fullscreen: false,
      tapped: false,
      isPdfLoaded: false,
      isPdfFullScreenLoaded: false,
      zoomStep: 0,

      isWarningVisible: null,
      isWarningContentVisible: null,
    };
  },
  mounted() {
    console.debug('mounted with  this.appliedPdfViewer', this.appliedPdfViewer, 'pdfViewer=', this.pdfViewer);
    if (this.displayPdfViewerHintMode === DISPLAY_PDF_VIEWER_HINT_MODE.display) {
      this.displayChangeViewerForSeconds(15);
    } else if (this.displayPdfViewerHintMode === DISPLAY_PDF_VIEWER_HINT_MODE.button) {
      this.hideWarningHint();
    }

    if (this.appliedPdfViewer === PDFJS_EXPRESS) {
      this.loadPdfJsExpress();
    }
  },
  computed: {
    ...mapState('profile', ['userProfile']),
    ...mapState({
      isConnected: (state) => state.isConnected,
    }),
    pdfZoom() {
      const defaultPdfZoom = this.$store?.state?.configs?.features?.defaultPdfZoom;
      return defaultPdfZoom ? defaultPdfZoom : 'page-width';
    },
    fullscreenBtnStyle() {
      return {
        'right-btn': true,
        'right-btn-hidden': this.hidePdfOpenIcon,
      };
    },
    appliedPdfViewer() {
      if (this.pdfViewer === PDFJS_EXPRESS && this.isConnected) return PDFJS_EXPRESS;

      return PDFJS;
    },
    isPdfJs() {
      return this.appliedPdfViewer === PDFJS;
    },
    isPdfjsExpress() {
      return this.appliedPdfViewer === PDFJS_EXPRESS;
    },

    pdfIssuesWarningStyle() {
      if (this.displayPdfViewerHint) {
        if (this.isWarningVisible) {
          return {
            display: 'flex',
            height: '270px',
            width: '100%',
            left: 0,
            marginLeft: 0,
            marginRight: 0,
            borderRadius: 0,
          };
        } else {
          return {
            borderBottomLeftRadius: '10px',
            borderBottomRightRadius: '10px',
            height: '10px',
            marginLeft: '35%',
            marginRight: '35%',
            width: '30%',
            // position: 'absolute',
          };
        }
      }

      return { display: 'none' };
    },
    displayPdfViewerHint() {
      return this.displayPdfViewerHintMode !== DISPLAY_PDF_VIEWER_HINT_MODE.none;
    },
    pdfViewer: {
      get() {
        return this.userProfile.pdfViewer || PDFJS;
      },
      set(value) {
        this.setPdfViewer(value);
      },
    },
    displayPdfChangeViewer: {
      get() {
        return this.userProfile.displayPdfChangeViewer;
      },
      set(value) {
        this.setDisplayPdfChangeViewer(value);
      },
    },
    displayPdfViewerHintMode: {
      get() {
        return this.userProfile.displayPdfViewerHintMode;
      },
      set(value) {
        this.setDisplayPdfViewerHintMode(value);
      },
    },

    iframeSrc() {
      // Search with #search=url encoded search strings (multiple search words separated by space)

      // The version pdfjs-3.5.141-legacy-dist breaks functionality on older samsung devices
      // It is not clear which changes caused the issues
      // Only upgrade after thorough testing with older devices like Device Jeremy or old samsung Marcel
      const filename = this.downloadFileName ? `&filename=${this.downloadFileName}` : '';
      let url = `${config.webAppBaseUrl}pdfjs-3.4.120-legacy-dist/web/viewer.html?file=${this.url}${filename}&hidePdfOpenIcon=${
        this.hidePdfOpenIcon ? 'yes' : 'no'
      }&hidePdfDownloadIcon=${this.hidePdfDownloadIcon ? 'yes' : 'no'}#zoom=${this.pdfZoom}`;
      if (this.page && this.page !== 'undefined' && Number(this.page)) url += `&page=${this.page}`;

      if (this.search && this.search !== '') url += `&search=${this.search}`;

      return url;
    },
  },
  methods: {
    downloadFile,
    ...mapMutations('profile', ['setPdfViewer', 'setDisplayPdfChangeViewer', 'setDisplayPdfViewerHintMode']),

    togglePdfViewer() {
      this.pdfViewer = this.pdfViewer === PDFJS ? PDFJS_EXPRESS : PDFJS;
    },
    toggleWarningHint() {
      if (this.isWarningVisible) {
        this.hideWarningHint();
      } else {
        this.showWarningHint();
      }
    },
    hideWarningHint() {
      this.isWarningVisible = false;
      this.isWarningContentVisible = false;
    },
    showWarningHint() {
      clearTimeout(displayChangeViewerTimer);
      this.isWarningVisible = true;
      this.isWarningContentVisible = false;
      setTimeout(() => {
        this.isWarningContentVisible = true;
      }, 500);
    },
    clearDisplayChangeViewerTimer() {
      console.debug('clearDisplayChangeViewerTimer');
      clearTimeout(displayChangeViewerTimer);
    },
    displayChangeViewerForSeconds(seconds) {
      clearTimeout(displayChangeViewerTimer);

      this.showWarningHint();
      displayChangeViewerTimer = setTimeout(() => {
        this.hideWarningHint();
      }, seconds * 1000);
    },

    loadPdfJsExpress() {
      console.debug('loadPdfJsExpress', this.appliedPdfViewer);
      if (this.appliedPdfViewer === PDFJS_EXPRESS) {
        let pdfJsExpressEl = this.$refs['pdfJsExpressEl'];
        if (!pdfJsExpressEl) {
          console.warn('pdfJsExpressEl is not defined');
        } else {
          if (this.pdfJsExpress) {
            this.pdfJsExpress.UI.loadDocument(this.url);
          } else {
            console.debug('CREATE NEW PDFJSEXPRESS INSTANNCE -------------------');
            PDFJSExpress(
              {
                path: '/pdfjsexpress',
                licenseKey: 'Qib084LL4DDUnEE9k18g',
                initialDoc: this.url,
              },
              document.getElementById('pdfJsExpressEl')
            )
              .then((instance) => {
                // use APIs here
                this.pdfJsExpress = instance;
                // instance.UI.setLanguage('de');

                instance.UI.openElements(['menuOverlay', 'fullScreenButton']);
                // disable pan tool
                instance.UI.disableElements(['panToolButton']);

                // instance.UI.setTheme('dark');
              })
              .catch((err) => {
                console.warn('Failed to initialize pdfjsexpressel', err);
              });
          }
        }
      }
    },
    toggleFullscreenMode() {
      console.debug('toggleFullscreenMode');
      this.fullscreen = !this.fullscreen;

      this.$store.commit('setIsToolbarsHidden', this.fullscreen);
    },

    pdfJsLoaded(pdfIframeId) {
      let iframe = document.getElementById(pdfIframeId);
      let iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

      scaleElement = iframeDocument.getElementById('viewerContainer');
      let toolbarContainer = iframeDocument.getElementById('toolbarContainer');
      toolbarContainer.style.position = 'fixed'; // otherwise the toolbar will partially move with the document
      scaleElement.style.touchAction = 'manipulation';

      // remove annotation buttons
      let editorModeButtonsEl = iframeDocument.getElementById('editorModeButtons');
      editorModeButtonsEl.style.display = 'none';
    },
  },
  watch: {
    url(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (this.displayPdfViewerHintMode === DISPLAY_PDF_VIEWER_HINT_MODE.display) {
          this.displayChangeViewerForSeconds(15);
        } else if (this.displayPdfViewerHintMode === DISPLAY_PDF_VIEWER_HINT_MODE.button) {
          this.hideWarningHint();
        }

        if (this.pdfViewer === PDFJS_EXPRESS) {
          this.loadPdfJsExpress();
        }
      }
    },
    appliedPdfViewer(newValue, oldValue) {
      if (newValue === PDFJS_EXPRESS) {
        this.loadPdfJsExpress();
      }
    },
  },
};
</script>

<style>
.pdf-container {
  transition: all 0.75s ease-in-out;
  display: flex;
  flex-direction: column;
}

#pdfJsExpressEl,
#pdfjs {
  display: flex;
  flex: 1;
}

iframe {
  overflow: hidden;
  width: calc(100% + 0px); /* +40px corrects pdf viewer padding on the right */

  background: white;
  border: 0;
}

.right-btn {
  position: fixed !important;
  right: 10px !important;
  height: 30px !important;
  width: 30px !important;
  bottom: 30px !important;
}

.right-btn-hidden {
  right: 164px !important;
  top: 8px !important;
  font-size: 28px !important;
}

.pdf-viewer-issues {
  display: flex;
  //position: relative; //left: 0; //top: 0; transition: all 600ms; overflow: hidden; align-items: center; justify-items: center; flex-direction: column;
}

.pdf-viewer-issues-alert {
  height: 40px;
  width: 40px;
  position: absolute;
  border-radius: 20px;
  transform: translateY(-20px);
  left: calc(100% - 25px);
  top: 80px;
  overflow: hidden;
  align-items: center;
  justify-items: center;
  flex-direction: column;
}

.theme--light .pdf-viewer-issues {
  background: #ef9f4d;
  border-right: 1px solid #b0b0b0;
}

.theme--dark .pdf-viewer-issues {
  background: #202020;
  border-right: 1px solid #505050;
}

.warning-close {
  position: absolute;
  right: 0;
  top: 5px;
  height: 30px;
  width: 30px;
}

.centered {
  display: flex;
  justify-content: center;
}
</style>
